/**
 * Connection-creation pure helpers.
 *
 * Pure helpers lifted from ConnectionCreationPanel so the auto-create-mode
 * decision and the app-context guard are unit-testable and the panel keeps
 * only rendering/orchestration concerns. Behavior-preserving.
 */
import type { ConnectionParameterSet } from '@/types/connectionParameters';
import { isParameterHidden, ParameterType } from '@/types/connectionParameters';
import type { ConnectionReference } from '@/types/connectionReference';

/**
 * Key under which a service-provider connection stores the id of the OAuth
 * connection supplying its credential. The backend hardcodes this name when it
 * generates the connector manifest, so it is the fallback for a parameter set
 * that declares no `oauthConnection` parameter of its own.
 */
export const OAUTH_CONNECTION_PARAMETER_KEY = 'oauthConnectionId';

/**
 * Auto-create mode for connections:
 * - `oauth`: connector only needs OAuth, auto-trigger the OAuth flow
 * - `simple`: connector has no parameters, create immediately
 * - `manual`: connector needs user input, show the full form
 */
export type AutoCreateMode = 'oauth' | 'simple' | 'manual';

/**
 * Detect whether a connector can be auto-created without user input.
 *
 * `displayName` is ignored (handled by the prominent name field). A set with no
 * other parameters is `simple`; a set whose only visible parameters are
 * OAuth/managed-identity is `oauth`; anything else is `manual`.
 *
 * An `oauthConnection` set is deliberately `manual`: its sign-in has to be named
 * before it can be created, so the form is shown to collect that name.
 */
export function detectAutoCreateMode(
  parameterSet: ConnectionParameterSet | undefined,
): AutoCreateMode {
  if (!parameterSet) return 'manual';

  const params = parameterSet.parameters ?? {};
  const paramEntries = Object.entries(params);

  // Filter out displayName as it's handled by the prominent field
  const nonNameParams = paramEntries.filter(([key]) => key !== 'displayName');

  // No parameters (besides displayName) = could be simple auto-create
  // But we still need to check if it has OAuth
  if (nonNameParams.length === 0) {
    return 'simple';
  }

  // Check if all non-hidden parameters are OAuth
  const visibleNonOAuthParams = nonNameParams.filter(([, param]) => {
    // Skip hidden parameters (uses shared helper — checks hidden, hideInUI)
    if (isParameterHidden(param)) return false;

    // Skip internal-visibility parameters
    if (param.visibility === 'internal') return false;

    // Check if it's an OAuth or managed identity parameter
    const isOAuth = param.type === ParameterType.OAuthSetting;
    const isMI = param.type === ParameterType.ManagedIdentity;
    return !isOAuth && !isMI;
  });

  // If no visible non-OAuth parameters, we can auto-trigger OAuth
  if (visibleNonOAuthParams.length === 0) {
    // But only if there IS an OAuth parameter
    const hasOAuth = nonNameParams.some(([, param]) => param.type === ParameterType.OAuthSetting);
    if (hasOAuth) return 'oauth';
  }

  return 'manual';
}

/**
 * The parameter set a connection actually uses, out of the ones its connector
 * declares. A record saved without a set name predates multi-auth and takes the
 * first; a name that matches nothing means the manifest moved on, which is not
 * the same as "the first one will do".
 */
export function selectConnectionParameterSet(
  parameterSets: ConnectionParameterSet[],
  parameterSetName: string | undefined,
): ConnectionParameterSet | undefined {
  if (!parameterSetName) return parameterSets[0];
  return parameterSets.find((set) => set.name === parameterSetName);
}

/**
 * True when the set's credential comes from a separate OAuth connection rather
 * than from values typed into the form. Such a set contributes a single
 * `oauthConnection` entry holding that connection's name.
 */
export function isOAuthConnectionParameterSet(
  parameterSet: ConnectionParameterSet | undefined,
): boolean {
  if (!parameterSet) return false;
  return Object.values(parameterSet.parameters ?? {}).some(
    (param) => param.type === ParameterType.OAuthConnection,
  );
}

/**
 * Name the parameter set gives its `oauthConnection` parameter, which is where
 * the OAuth connection id has to be persisted for the runtime to find it.
 */
export function getOAuthConnectionParameterKey(
  parameterSet: ConnectionParameterSet | undefined,
): string {
  const declared = Object.entries(parameterSet?.parameters ?? {}).find(
    ([, param]) => param.type === ParameterType.OAuthConnection,
  );

  return declared?.[0] ?? OAUTH_CONNECTION_PARAMETER_KEY;
}

/**
 * Build the reference for a service-provider connection that holds the resource
 * id of the OAuth connection supplying its credential. The runtime resolves that
 * id to an access token when the operation runs, so no secret or runtime URL is
 * persisted here. Any other values the parameter set declares are persisted
 * alongside it.
 */
export function buildOAuthConnectionHolderReference(params: {
  connectorId: string;
  connectionName: string;
  oauthConnectionId: string;
  oauthParameterKey?: string;
  parameterSetName?: string;
  parameterValues?: Record<string, unknown>;
}): ConnectionReference {
  const {
    connectorId,
    connectionName,
    oauthConnectionId,
    oauthParameterKey,
    parameterSetName,
    parameterValues,
  } = params;

  return {
    api: { id: connectorId },
    connection: { id: '' },
    connectionName,
    connectionProperties: {
      ...parameterValues,
      [oauthParameterKey ?? OAUTH_CONNECTION_PARAMETER_KEY]: oauthConnectionId,
      ...(parameterSetName ? { _parameterSetName: parameterSetName } : {}),
    },
  };
}

/**
 * Assert that a project and application are selected before a
 * service-provider connection operation. Throws a user-facing error naming the
 * blocked `action` (e.g. "test connection", "create connection") otherwise.
 */
export function requireAppContext(
  project: string | null,
  app: string | null,
  action: string,
): { project: string; app: string } {
  if (!project || !app) {
    throw new Error(
      `Cannot ${action}: no project / application selected. ` +
      'Open an application before creating a service-provider connection.'
    );
  }
  return { project, app };
}
